/**
 * =============================================================================
 * WlBillingKpiCards
 *
 * Grid de 4 KPIs principais da aba Cobrança (WL Financeiro):
 *   1. Receita Total   — parcelas com vencimento no mês (regime de competência)
 *   2. Total Recebido  — parcelas pagas por data_pagamento no mês (caixa)
 *   3. A Receber       — parcelas em aberto com vencimento >= hoje (no mês)
 *   4. Vencidos        — parcelas em aberto com vencimento < hoje (no mês)
 *
 * IMPORTANTE: Receita Total e Recebido usam dimensões diferentes por decisão
 * de negócio (competência × caixa) e portanto NÃO precisam fechar
 * aritmeticamente. Isso está explicitado nos tooltips.
 *
 * Reaproveita o componente premium `CrmPremiumKpiCard` (mesmo padrão do
 * Dashboard de Tarefas / Indicações) para consistência visual entre módulos.
 *
 * @version 2.2.0
 * RELEASE:
 *   2026-07-22 v1.0.0 - Versão inicial (cards horizontais próprios).
 *   2026-07-22 v2.0.0 - Migração para CrmPremiumKpiCard.
 *   2026-07-22 v2.1.0 - Tooltips atualizados: Receita Total agora é por
 *                       vencimento (competência) e Total Recebido por
 *                       data_pagamento (caixa). Explicita a divergência.
 *   2026-07-22 v2.2.0 - Cards clicáveis: abrem `WlBillingKpiDetailModal`
 *                       com a lista de parcelas que compõem o valor, para
 *                       auditoria de tenants.
 * =============================================================================
 */
import { useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Wallet, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { CrmPremiumKpiCard } from '@/components/crm/dashboard/premium';
import type {
  WlBillingKpisPayload,
  WlBillingKpi,
} from '@/hooks/wl/useWlBillingKpis';
import { WlBillingKpiDetailModal } from './WlBillingKpiDetailModal';

interface Props {
  data?: WlBillingKpisPayload;
  isLoading?: boolean;
}

// Formatador BRL (pt-BR)
const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(v || 0));

/** Helper: monta título com contagem de registros embutida. */
const withCount = (label: string, count: number) =>
  `${label} · ${count} ${count === 1 ? 'registro' : 'registros'}`;

/** Calcula progresso percentual limitado entre 0 e 100. */
const pct = (num: number, den: number) => {
  if (!den || den <= 0) return 0;
  const v = (num / den) * 100;
  return Math.max(0, Math.min(100, Math.round(v)));
};

export function WlBillingKpiCards({ data, isLoading }: Props) {
  // Estado do drill-down: qual KPI e label estão abertos no modal.
  const [drill, setDrill] = useState<{ kpi: WlBillingKpi; label: string } | null>(null);

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[130px] w-full" />
        ))}
      </div>
    );
  }

  const k = data.kpis;
  const receitaTotal = Number(k.receita_total.value || 0);
  const referenceMonth = data.reference_month ?? null;

  /** Handler genérico para abrir o modal de drill-down de um KPI. */
  const openDrill = (kpi: WlBillingKpi, label: string) => setDrill({ kpi, label });

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <CrmPremiumKpiCard
          title={withCount('Receita Total', k.receita_total.count)}
          value={fmtBRL(k.receita_total.value)}
          icon={Wallet}
          variant="success"
          progress={receitaTotal > 0 ? 90 : 0}
          tooltip="Soma do valor das parcelas cujo VENCIMENTO cai dentro do período selecionado (regime de competência). Exclui cobranças canceladas. Não fecha aritmeticamente com Total Recebido porque este último usa a data de PAGAMENTO (regime de caixa). Este valor também não é comparável ao MRR/ARR da aba Visão geral: MRR normaliza planos anuais em base mensal e considera só recorrência ativa; Receita Total mostra o faturamento bruto do mês (planos + add-ons + avulsos)."
          onClick={() => openDrill('receita_total', 'Receita Total')}
        />
        <CrmPremiumKpiCard
          title={withCount('Total Recebido', k.total_recebido.count)}
          value={fmtBRL(k.total_recebido.value)}
          icon={CheckCircle2}
          variant="purple"
          progress={pct(k.total_recebido.value, receitaTotal)}
          tooltip="Soma dos valores efetivamente pagos no período (parcelas com status 'pago' cuja DATA DE PAGAMENTO cai no mês). Regime de caixa: uma parcela que venceu em meses anteriores e foi paga no período entra aqui, e uma parcela que vence no período mas ainda não foi paga NÃO entra. Por isso pode ser maior ou menor que Receita Total."
          onClick={() => openDrill('total_recebido', 'Total Recebido')}
        />
        <CrmPremiumKpiCard
          title={withCount('A Receber', k.a_receber.count)}
          value={fmtBRL(k.a_receber.value)}
          icon={Clock}
          variant="info"
          progress={k.a_receber.value > 0 ? 60 : 0}
          tooltip="Soma das parcelas em aberto (pendente / aguardando pagamento / aguardando emissão) com vencimento FUTURO dentro do período. Representa o que ainda deve entrar sem estar atrasado."
          onClick={() => openDrill('a_receber', 'A Receber')}
        />
        <CrmPremiumKpiCard
          title={withCount('Vencidos', k.vencidos.count)}
          value={fmtBRL(k.vencidos.value)}
          icon={AlertTriangle}
          variant="warning"
          progress={pct(k.vencidos.value, receitaTotal)}
          tooltip="Soma das parcelas com status 'vencido' OU parcelas em aberto cuja data de vencimento já passou (< hoje), considerando o vencimento dentro do período. Indica inadimplência do mês."
          onClick={() => openDrill('vencidos', 'Vencidos')}
        />
      </div>

      {/* Modal de drill-down (compartilhado entre os 4 cards). */}
      <WlBillingKpiDetailModal
        open={!!drill}
        onClose={() => setDrill(null)}
        kpi={drill?.kpi ?? null}
        label={drill?.label ?? null}
        referenceMonth={referenceMonth}
      />
    </>
  );
}
